/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTour } from "hooks/useTour";

import {
  setUnitsList,
  toggleSelectedUnit,
} from "store/actions/select-unit-actions";

import * as religieApi from "../services/religueApi";
import { buildReligueModalInfos } from "../utils/buildReligueModalInfos";
import { getDay } from "../utils/dateHelpers";
import {
  insertTagManagerReligue,
  logReligueOpenSolicitacao,
} from "../utils/religueAnalytics";
import { StatusSuspension } from "../styles";
import type {
  ReligueSelectedUnit,
  UseReliguePageReturn,
} from "../types/religueHookReturn";

export function useReliguePage(): UseReliguePageReturn {
  const [loading, setLoading] = useState(false);
  const [loadingSolicitCase, setLoadingSolicitCase] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [openModalTurnOff, setOpenModalTurnOff] = useState(false);
  const [, setOpenModalWithLink] = useState(false);
  const [modalWithLink, setModalWithLink] = useState<any>();
  const [modalInfos, setModalInfos] = useState<any>();
  const [activeKey, setActiveKey] = useState<number>();
  const [email, setEmail] = useState("");
  const [buttonModalTurnOff, setButtonModalTurnOff] = useState("request");
  const [hasOverdue, setHasOverdue] = useState(false);
  const [loadingButtonValidateToken, setLoadingButtonValidateToken] =
    useState(false);
  const [suspensions, setSuspensions] = useState<any[]>([]);
  const [reconnections, setReconnections] = useState<any[]>([]);

  const { selectedUnit } = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const requestHasOverDue = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    try {
      const response = await religieApi.fetchUnitsOverdueStatus(
        document!,
        instanceId
      );
      const { data } = response;
      setHasOverdue(data[0].overdueInvoicesCount > 1);
    } catch (err: any) {
      const { response } = err;
      toast.error(response?.data?.message);
    }
  };

  const requestUserInfo = async (): Promise<void> => {
    try {
      const {
        data: { email: emailData },
      } = await religieApi.fetchCustomerMe();
      if (emailData) {
        setEmail(emailData);
      } else {
        setButtonModalTurnOff("updateEmail");
      }
    } catch (err: any) {
      const { response } = err;
      toast.error(
        response?.data?.message || "Não foi possível obter o contato do usuário"
      );
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      await requestUserInfo();
      if (!selectedUnit.hasCurrentHouseholder) {
        history.push("/portal");
      }
    })();
  }, [selectedUnit]);

  const openUlly = (): void => {
    const chatbotButton = document.querySelector("#hi-custom-widget") as any;
    if (chatbotButton) {
      chatbotButton.click();
    }
  };

  const handleTrustSolicit = (): void => {
    requestPostRecconections(true);
    setOpenModal(false);
  };

  const handleModalInformations = (status: string): any =>
    buildReligueModalInfos(status, {
      handleTrustSolicit,
      openUlly,
      setOpenModal,
    });

  const handleSolicitCase = async (reason: string): Promise<void> => {
    const contactIdSf = localStorage.getItem("@UltragazEmpresarial:contactIdSf");
    const { instanceId, unitId } = selectedUnit;
    const { data: dataCusomersMe } = await religieApi.fetchCustomerMe();
    const customerSiteId = dataCusomersMe?.customerAddresses?.find(
      (data: { unitId?: string }) => data?.unitId === unitId
    )?.siteId;

    const bodyToken = {
      customerSiteId,
      instanceId,
      contactIdSf,
      reason,
    };

    try {
      await religieApi.postSuspension(bodyToken);
      setOpenModalTurnOff(false);
      setModalWithLink({
        open: true,
        description: (
          <p>
            {reason === "Bloqueio"
              ? "O bloqueio de fornecimento temporário foi solicitado e será efetivado em até 5 dias úteis!"
              : "O desligue de fornecimento por saída do imóvel foi solicitado e será efetivado em até 5 dias úteis!"}
          </p>
        ),
      });
    } catch (err: any) {
      const data = err?.response?.data;
      setOpenModalTurnOff(false);
      setModalWithLink({
        open: true,
        description: <p>{data?.message}</p>,
      });
    }
  };

  const requestPostRecconections = async (trusted: boolean): Promise<void> => {
    setButtonLoading(true);

    const document = localStorage.getItem("@UltragazEmpresarial:document");
    try {
      const { instanceId } = selectedUnit;
      await religieApi.postReconnection({
        document,
        trusted,
        unitInstanceId: instanceId,
      });
      if (trusted) {
        setOpenModalWithLink(true);
        insertTagManagerReligue("inadimplente");
      } else {
        setModalInfos(handleModalInformations("201"));
        setOpenModal(true);
        insertTagManagerReligue("201");
      }
    } catch (error: any) {
      if (error.response) {
        const { response } = error;
        const { data } = response;
        const code = data.code || "any";
        setModalInfos(handleModalInformations(code));
        setOpenModal(true);
        insertTagManagerReligue(code);
      } else {
        setModalInfos(handleModalInformations("any"));
        setOpenModal(true);
        insertTagManagerReligue("any");
      }
    }

    await requestGetRecconections();
    await requestGetSuspensions();
    setButtonLoading(false);
  };

  const handleSolicit = (): void => {
    logReligueOpenSolicitacao();

    if (hasOverdue) {
      setModalInfos(handleModalInformations("newRule"));
      setOpenModal(true);
      return;
    }

    requestPostRecconections(false);
  };

  const renderBannerStatus = useMemo(() => {
    const statusArray = [...reconnections, ...suspensions];
    const { status: statusUnit } = selectedUnit;

    if (statusArray?.length) {
      const status = statusArray
        ?.filter((data) => data?.createdDate)
        ?.sort((a, b) => {
          // @ts-ignore
          return new Date(b?.createdDate) - new Date(a?.createdDate);
        })[0];

      let text = "";

      switch (status?.subject) {
        case "Religue":
        case "Ligue":
          text = "LIGUE";
          break;
        default:
          text = "SUSPENSÃO";
          break;
      }

      if (
        status &&
        ((status.subject !== "Religue" && statusUnit === "ON") ||
          (status.subject === "Religue" && statusUnit !== "ON"))
      ) {
        return (
          <StatusSuspension>
            <AiOutlineInfoCircle />
            <span>
              {`Há uma solicitação de ${text} de sua unidade em andamento.`}
            </span>
          </StatusSuspension>
        );
      }
    }

    return false;
  }, [reconnections, suspensions, selectedUnit]);

  const sendToken = async (value: string): Promise<any> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");
    const siteId = localStorage.getItem("@UltragazEmpresarial:customerSiteId");

    const bodyContact = {
      contact: value,
      contactType: "EMAIL",
      siteId,
    };

    try {
      const createContact = await religieApi.createEmailContact(
        userId!,
        bodyContact
      );

      const contactId = createContact.headers.location.split("/").pop();

      const bodyToken = {
        contactId,
        tokenType: "REGISTER",
      };

      const responseToken = await religieApi.postRegisterToken(
        userId!,
        bodyToken
      );

      toast.warn(`Enviamos um código de verificação para ${value}`);
      return responseToken;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Ocorreu um erro interno na aplicação"
      );
      return false;
    }
  };

  const validationToken = async (
    token: string,
    _email: string
  ): Promise<any> => {
    const userId = localStorage.getItem("@UltragazEmpresarial:userId");

    let success;
    try {
      await religieApi.postValidateToken(userId!, token);
      success = true;
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? "Ocorreu um erro interno na aplicação"
      );
      success = false;
    }

    return success;
  };

  const handleSolicitBlocked = async (): Promise<void> => {
    setLoadingSolicitCase(true);
    await handleSolicitCase("Bloqueio");
    await requestGetRecconections();
    await requestGetSuspensions();
    setLoadingSolicitCase(false);
  };

  const handleSolicitTurnOff = async (): Promise<void> => {
    setLoadingSolicitCase(true);
    await handleSolicitCase("Fechamento");

    try {
      await religieApi.patchUnitDeliveryType(selectedUnit.unitId);
      const response = await religieApi.fetchCustomerMeUnits();
      const { data } = response;
      dispatch(setUnitsList(data));
      const { instanceId } = selectedUnit;
      const unitFilted = data.find(
        (unit: { instanceId: string }) => instanceId === unit.instanceId
      );
      if (unitFilted) {
        dispatch(toggleSelectedUnit(unitFilted));
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Ocorreu um erro interno na aplicação"
      );
    }

    setOpenModalTurnOff(false);
    await requestGetRecconections();
    await requestGetSuspensions();
    setLoadingSolicitCase(false);
  };

  const requestGetSuspensions = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { unitId } = selectedUnit;
    const { data: dataCusomersMe } = await religieApi.fetchCustomerMe();
    const customerSiteId = dataCusomersMe?.customerAddresses?.find(
      (data: { unitId?: string }) => data?.unitId === unitId
    )?.siteId;

    const dateBegin = new Date();
    dateBegin.setDate(dateBegin.getDate() - 4);
    const yearBegin = dateBegin.getFullYear();
    const monthBegin = getDay(dateBegin.getMonth() + 1);
    const dayBegin = getDay(dateBegin.getDate());

    const dateEnd = new Date();
    const yearEnd = dateEnd.getFullYear();
    const monthEnd = getDay(dateEnd.getMonth() + 1);
    const dayEnd = getDay(dateEnd.getDate());

    const params = {
      "customer-site-id": customerSiteId,
      document: document!,
      "begin-date": `${yearBegin}-${monthBegin}-${dayBegin}`,
      "end-date": `${yearEnd}-${monthEnd}-${dayEnd}`,
    };

    try {
      const response = await religieApi.fetchSuspensions(params);
      const { data } = response;
      setSuspensions(data);
    } catch (error: any) {
      const { response } = error;
      toast.error(
        response?.data?.message || "Não foi possível obter as suspensões"
      );
    }
  };

  const requestGetRecconections = async (): Promise<void> => {
    const dateBegin = new Date();
    dateBegin.setDate(dateBegin.getDate() - 4);
    const yearBegin = dateBegin.getFullYear();
    const monthBegin = getDay(dateBegin.getMonth() + 1);
    const dayBegin = getDay(dateBegin.getDate());

    const dateEnd = new Date();
    const yearEnd = dateEnd.getFullYear();
    const monthEnd = getDay(dateEnd.getMonth() + 1);
    const dayEnd = getDay(dateEnd.getDate());

    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    const params = {
      "unit-instance-id": instanceId,
      document: document!,
      "begin-date": `${yearBegin}-${monthBegin}-${dayBegin}`,
      "end-date": `${yearEnd}-${monthEnd}-${dayEnd}`,
    };

    try {
      const response = await religieApi.fetchReconnections(params);
      const { data } = response;
      setReconnections(data);
    } catch (err: any) {
      if (err.response) {
        const erro = err.response.data;
        if (erro) {
          const { message } = err.response.data;
          if (err.response.status !== 401) {
            toast.error(message);
          }
        } else if (err.response.status !== 401) {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await requestGetRecconections();
      await requestGetSuspensions();
      await requestHasOverDue();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    setTourType("religue");
    setSteps(1);
  }, [showTour, setTourType, setSteps]);

  const value: UseReliguePageReturn = {
    loading,
    loadingSolicitCase,
    buttonLoading,
    openModal,
    setOpenModal,
    openModalInfo,
    setOpenModalInfo,
    openModalTurnOff,
    setOpenModalTurnOff,
    modalWithLink,
    setModalWithLink,
    modalInfos,
    activeKey,
    setActiveKey,
    email,
    setEmail,
    openUlly,
    buttonModalTurnOff,
    setButtonModalTurnOff,
    loadingButtonValidateToken,
    setLoadingButtonValidateToken,
    suspensions,
    reconnections,
    selectedUnit: selectedUnit as ReligueSelectedUnit,
    showTour,
    tourStep,
    tourType,
    renderBannerStatus,
    handleSolicit,
    handleSolicitBlocked,
    handleSolicitTurnOff,
    sendToken,
    validationToken,
  };
  return value;
}
